import { useCallback, useEffect, useRef, useState } from 'react';
import * as depositService from '@/supabase/deposit/service/depositService';
import type { PartialDepositEntry, PartialDepositGroups } from '@/supabase/deposit/types/deposit';

export function usePartialDepositOverview(userId: string | null) {
  const [groups, setGroups] = useState<PartialDepositGroups | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seqRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const refresh = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) return;
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      // torn(페이지 간 서버 지문 변동 = 설정 세대 혼합) 1회 재조회, 2회 연속은 fail-closed —
      // 서로 다른 설정 세대의 임계값 혼합 커밋 금지 (PR C 지문 재시도 계약 보존. 임계값이 DB 단일
      // 소스가 되며(§16.3) 클라 config 로드·재계산은 제거 — 비교 축이 서버-클라 → 페이지-간으로 이동).
      for (let attempt = 0; attempt < 2; attempt++) {
        const overview = await depositService.fetchPartialDepositOverview();
        if (seq !== seqRef.current || userIdRef.current !== uid) return;  // superseded
        if (overview === null) { setError(true); setLoading(false); return; }
        if (overview.torn) {
          if (attempt === 0) continue;   // torn — 재조회
          setError(true);                // fail-closed
          setLoading(false);
          return;
        }
        // 지문 운반 계약: convert는 entry에 부착된 서버 지문만 사용(액션 시점 재계산 금지).
        // 지문은 임계값과 같은 overview 스냅샷에서 옴 — null(행 없음 외 방어)은 ''로 두어
        // convert의 서버 지문 대조에서 P0003 settings_changed로 fail-closed.
        const fp = overview.serverFingerprint ?? '';
        const entries: PartialDepositEntry[] = overview.rows.map((r) => ({ ...r, settingsFingerprint: fp }));
        // 3티어(스펙 §16.9): settleReady 맨 위(전액 도착 = 마감 유도), safetyLane(자동 전환 놓침),
        // inProgress는 오래된 순(오래 안 들어온 돈이 위) — settleReady·safetyLane은 RPC id DESC 유지.
        setGroups({
          settleReady: entries.filter((e) => e.status === '부분 입금' && e.remaining <= 0),
          safetyLane: entries.filter((e) => e.isSafetyLane),
          inProgress: entries.filter((e) => e.status === '부분 입금' && e.remaining > 0)
            .sort((a, b) => a.orderDate.localeCompare(b.orderDate) || a.orderId - b.orderId),
        });
        setError(false);
        setLoading(false);
        return;
      }
    } catch {
      if (seq !== seqRef.current) return;
      setError(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {                                                    // mount + 유저 전환(로그아웃 포함)
    // 유저 전환 즉시 이전 셀러 데이터 제거 (교차 노출 방지) + 진행 중 요청 무효화
    seqRef.current++;
    setGroups(null);
    setError(false);
    setLoading(false);
    if (userId) void refresh();
  }, [refresh, userId]);
  useEffect(() => {                                                    // 탭 포커스 복귀 재조회 (스펙 §10)
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  // mutation 공통: RPC가 throw(P0001·네트워크)해도 finally로 refresh를 보장 —
  // 선-bump로 무효화된 in-flight refresh가 loading=true를 남기는 고착 방지 (codexloop PR C R1-P1-5).
  // convert = 안전 레인(확인 필요) 수동 fallback(§16.8). 서버 threshold가 권위 — entry의
  // paidThreshold는 진단용 인자로만 전달(5인자 시그니처 호환).
  const convert = useCallback(async (entry: PartialDepositEntry) => {
    seqRef.current++;
    try {
      return await depositService.convertPaidOrderToPartial(
        entry.orderId, entry.totalPrice, entry.shippingFeeOverride, entry.paidThreshold, entry.settingsFingerprint);
    } finally {
      await refresh();
    }
  }, [refresh]);

  // settle = up-manual 마감(§16.7). ack=false 첫 호출은 프로브 겸 마감 — 서버가 미달로 판정하면
  // {ok:false, reason:'requires_manual_override'} 구조화 반환(mutation 없음) → UI 확인 다이얼로그 →
  // ack=true 재시도. 충분하면 첫 호출에서 즉시 마감(1클릭). 프로브도 마감 가능성이 있으므로
  // 선-bump + finally-refresh 패턴 동일 적용.
  const settle = useCallback(async (entry: PartialDepositEntry, ackUnderpaid: boolean) => {
    seqRef.current++;
    try {
      return await depositService.settlePartialOrder(
        entry.orderId, entry.totalPrice, entry.shippingFeeOverride, entry.paidTotalSnapshot, ackUnderpaid);
    } finally {
      await refresh();
    }
  }, [refresh]);

  return { groups, loading, error, refresh, convert, settle };
}
